import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCrypto } from '../contexts/CryptoContext';
import { create } from '../services/beneficiaryService';
import { getDistinctCategories } from '../services/vaultService';
import BeneficiaryRelationPicker from '../components/BeneficiaryRelationPicker';
import PrioritySelector, { PRIORITY_CONFIG } from '../components/PrioritySelector';
import AttributionSelector from '../components/AttributionSelector';
import { TRANSMISSION_CONFIG } from '../config/transmissionConfig';
import { DESIGNATOR_CONSENT_TEXT } from '../config/rgpdConsentConfig';

const S = '#00F5A0';
const G = '#8892A4';

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '12px 14px',
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 10,
  color: '#fff',
  fontSize: 14,
  outline: 'none',
};

const SectionLabel = ({ children, style = {} }) => (
  <p style={{ fontWeight: 600, fontSize: 12, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.1em', margin: '0 0 12px', ...style }}>
    {children}
  </p>
);

const Field = ({ label, children, style = {} }) => (
  <div style={{ marginBottom: 20, ...style }}>
    <label style={{ display: 'block', fontSize: 13, color: G, marginBottom: 8, fontWeight: 500 }}>{label}</label>
    {children}
  </div>
);

const AddBeneficiaryPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { masterKey } = useCrypto();

  const [form, setForm] = useState({ lastName: '', firstName: '', email: '', phone: '', relation: null, priority: 1, message: '' });
  const [notifyOnAdd, setNotifyOnAdd] = useState(false);
  const [designatorConsent, setDesignatorConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  // Attribution state
  const [availableCategories, setAvailableCategories] = useState([]);
  const [loadingState, setLoadingState] = useState('loading');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [actExplicit, setActExplicit] = useState(false);
  const [attributionDirty, setAttributionDirty] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    (async () => {
      setLoadingState('loading');
      const result = await getDistinctCategories();
      if (cancelled) return;
      if (result.success) {
        setAvailableCategories(result.data.filter(cat => TRANSMISSION_CONFIG[cat]));
        setLoadingState('ready');
      } else {
        setLoadingState('error');
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isAuthenticated) { navigate('/login'); return null; }

  const handleCategoriesChange = (next) => {
    setAttributionDirty(true);
    setSelectedCategories(next);
  };

  const handleActExplicitChange = (next) => {
    setAttributionDirty(true);
    setActExplicit(next);
  };

  const isAttrRequired = loadingState === 'ready' && selectedCategories.length > 0;
  const isAttrValid    = !isAttrRequired || actExplicit;
  const submitDisabled = submitting || !isAttrValid || !designatorConsent;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim() || !form.phone.trim()) {
      setError('Le prénom, le nom, l\'email et le téléphone sont obligatoires.');
      return;
    }
    setSubmitting(true);
    setError('');
    const name = `${form.firstName.trim()} ${form.lastName.trim()}`;
    const payload = { ...form, name };
    if (attributionDirty) payload.access_categories = selectedCategories;
    payload.designator_consent = designatorConsent;
    const result = await create(payload, masterKey);
    if (result.success) {
      if (notifyOnAdd) {
        try {
          const { sendBeneficiaryNotification } = await import('../services/emailService');
          await sendBeneficiaryNotification(result.data, result.token, window.location.origin);
        } catch { /* dégradation gracieuse */ }
      }
      setSuccess({ ...result.data, notified: notifyOnAdd });
    } else {
      setError(result.error || 'Erreur lors de l\'ajout.');
    }
    setSubmitting(false);
  };

  if (success) {
    return (
      <div style={{ minHeight: '100vh', background: '#0A1628', color: '#fff', paddingTop: 80, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ maxWidth: 440, width: '100%', padding: '0 24px', textAlign: 'center' }}>
          <div style={{ width: 64, height: 64, background: 'rgba(0,245,160,0.1)', border: '1px solid rgba(0,245,160,0.25)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, color: S, margin: '0 auto 24px' }}>✓</div>
          <h2 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 10px' }}>Bénéficiaire ajouté</h2>
          <p style={{ color: G, fontSize: 14, margin: '0 0 6px' }}>
            <strong style={{ color: '#fff' }}>{success.name}</strong> sera contacté·e en{' '}
            <strong style={{ color: PRIORITY_CONFIG[success.priority]?.dot }}>Priorité {success.priority}</strong>.
          </p>
          <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13, margin: '0 0 32px', lineHeight: 1.6 }}>
            Il·elle recevra vos consignes dès que SafeHeir détecte une absence de check-in.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <button
              onClick={() => {
                setSuccess(null);
                setForm({ firstName: '', lastName: '', email: '', phone: '', relation: null, priority: 1, message: '' });
                setNotifyOnAdd(false);
                setDesignatorConsent(false);
                setError('');
                setSelectedCategories([]);
                setActExplicit(false);
                setAttributionDirty(false);
              }}
              style={{ background: S, color: '#0A1628', border: 'none', borderRadius: 10, padding: '13px 0', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
              + Ajouter un autre bénéficiaire
            </button>
            <button onClick={() => navigate('/beneficiaries')}
              style={{ background: 'rgba(255,255,255,0.06)', color: '#fff', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '13px 0', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
              Retour à mes bénéficiaires
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0A1628', color: '#fff', paddingTop: 80 }}>
      {/* Header */}
      <div style={{ background: 'rgba(255,255,255,0.03)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <div style={{ maxWidth: 700, margin: '0 auto', padding: '20px 40px' }}>
          <button onClick={() => navigate('/beneficiaries')}
            style={{ background: 'none', border: 'none', color: G, cursor: 'pointer', fontSize: 13, padding: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
            ← Mes bénéficiaires
          </button>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: '12px 0 4px' }}>Ajouter un bénéficiaire</h1>
          <p style={{ margin: 0, color: G, fontSize: 14 }}>Cette personne sera contactée si vous ne répondez pas à vos check-ins.</p>
        </div>
      </div>

      <div style={{ maxWidth: 700, margin: '0 auto', padding: '40px 40px 80px' }}>
        {error && (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '12px 16px', marginBottom: 28, color: '#f87171', fontSize: 14 }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>

          {/* Section 1 — Coordonnées */}
          <section style={{ marginBottom: 36 }}>
            <SectionLabel>1 — Coordonnées</SectionLabel>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <Field label="Prénom *">
                  <input type="text" required value={form.firstName}
                    onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
                    placeholder="ex: Marie" style={inputStyle} autoComplete="given-name" />
                </Field>
              </div>
              <div style={{ flex: 1, minWidth: 200 }}>
                <Field label="Nom *">
                  <input type="text" required value={form.lastName}
                    onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
                    placeholder="ex: Dupont" style={inputStyle} autoComplete="family-name" />
                </Field>
              </div>
            </div>
            <Field label="Adresse email *">
              <input type="email" required value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                placeholder="marie.dupont@email.com" style={inputStyle} />
            </Field>
            <Field label="Téléphone *">
              <input type="tel" required value={form.phone}
                onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                placeholder="+33 6 12 34 56 78" style={inputStyle} />
            </Field>
          </section>

          {/* Section 2 — Lien de parenté */}
          <section style={{ marginBottom: 36 }}>
            <SectionLabel>2 — Lien de parenté</SectionLabel>
            <Field label="Relation (optionnel)">
              <BeneficiaryRelationPicker
                value={form.relation}
                onChange={(id) => setForm(f => ({ ...f, relation: id }))}
              />
            </Field>
          </section>

          {/* Section 3 — Ordre de contact */}
          <section style={{ marginBottom: 36 }}>
            <SectionLabel>3 — Ordre de contact</SectionLabel>
            <p style={{ fontSize: 13, color: G, margin: '0 0 16px', lineHeight: 1.6 }}>
              SafeHeir contacte d'abord la Priorité 1. Si elle ne répond pas sous 48 h, la Priorité 2 est alertée, puis la Priorité 3 sous 48h supplémentaires si la P2 ne répond pas non plus.
            </p>
            <Field label="Priorité">
              <PrioritySelector value={form.priority} onChange={n => setForm(f => ({ ...f, priority: n }))} />
            </Field>
          </section>

          {/* Section 4 — Éléments à transmettre */}
          <section style={{ marginBottom: 36 }}>
            <SectionLabel>4 — Éléments à transmettre (optionnel)</SectionLabel>
            <AttributionSelector
              availableCategories={availableCategories}
              selectedCategories={selectedCategories}
              actExplicit={actExplicit}
              onCategoriesChange={handleCategoriesChange}
              onActExplicitChange={handleActExplicitChange}
              loadingState={loadingState}
              disabled={submitting}
            />
          </section>

          {/* Section 5 — Message personnel */}
          <section style={{ marginBottom: 36 }}>
            <SectionLabel>5 — Message personnel (optionnel)</SectionLabel>
            <p style={{ fontSize: 13, color: G, margin: '0 0 16px', lineHeight: 1.6 }}>
              Ce texte sera joint à l'email de notification envoyé à cette personne.
            </p>
            <Field label="Votre message">
              <textarea value={form.message}
                onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                rows={5} placeholder="Si tu lis ce message, voici ce que j'aimerais que tu fasses…"
                style={{ ...inputStyle, resize: 'vertical', minHeight: 100 }} />
            </Field>
          </section>

          {/* Section 6 — Notification */}
          <section style={{ marginBottom: 40 }}>
            <SectionLabel>6 — Notification (optionnel)</SectionLabel>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '16px 18px', background: 'rgba(0,245,160,0.03)', border: '1px solid rgba(0,245,160,0.1)', borderRadius: 12 }}>
              <input
                type="checkbox"
                id="notifyBeneficiary"
                checked={notifyOnAdd}
                onChange={e => setNotifyOnAdd(e.target.checked)}
                style={{ marginTop: 2, flexShrink: 0, accentColor: S }}
              />
              <label htmlFor="notifyBeneficiary" style={{ fontSize: 13, color: G, lineHeight: 1.6, cursor: 'pointer' }}>
                <strong style={{ color: 'rgba(255,255,255,0.8)' }}>Envoyer maintenant un email d'information à cette personne</strong>
                <br />
                Un email neutre lui sera envoyé pour l'informer qu'il a été désigné sur SafeHeir. Il n'aura accès à aucune de vos données pour l'instant. Vous pourrez le lui envoyer plus tard depuis la page Bénéficiaires si vous préférez.
              </label>
            </div>
          </section>

          {/* Section 7 — Consentement RGPD (K81) */}
          <section style={{ marginBottom: 40 }}>
            <SectionLabel>7 — Consentement (obligatoire)</SectionLabel>
            <label
              htmlFor="designatorConsent"
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '16px 18px',
                background: designatorConsent ? 'rgba(0,245,160,0.03)' : 'rgba(255,255,255,0.02)',
                border: `1px solid ${designatorConsent ? 'rgba(0,245,160,0.25)' : 'rgba(255,255,255,0.07)'}`,
                borderRadius: 12,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              <input
                type="checkbox"
                id="designatorConsent"
                checked={designatorConsent}
                onChange={e => setDesignatorConsent(e.target.checked)}
                style={{ marginTop: 2, flexShrink: 0, accentColor: S }}
              />
              <span style={{ fontSize: 13, color: '#B0BEC5', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                {DESIGNATOR_CONSENT_TEXT}
              </span>
            </label>
          </section>

          {/* Submit */}
          <button type="submit" disabled={submitDisabled}
            style={{
              width: '100%', padding: '15px 0',
              background: submitDisabled ? 'rgba(0,245,160,0.4)' : S,
              color: '#0A1628', border: 'none', borderRadius: 12,
              fontWeight: 700, fontSize: 16,
              cursor: submitDisabled ? 'not-allowed' : 'pointer',
            }}>
            {submitting ? 'Enregistrement…' : 'Ajouter ce bénéficiaire'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddBeneficiaryPage;
